import { useMemo } from 'react';
import { TilemapEditor } from './TilemapEditor.js';
import { TerrainEditor } from '../terrain/TerrainEditor.js';
import { InputBindingsField } from '../settings/InputBindingsField.js';
import { AssetField } from './fields/AssetField.js';
import { resyncSceneAssets } from './AssetDrop.js';
import { CurveField } from './fields/CurveField.js';
import { GradientField } from './fields/GradientField.js';
import { ProjectSettings } from '../settings/ProjectSettings.js';
import { TextureImportEditor } from './TextureImportEditor.js';
import { ImpostorBakeButton } from './ImpostorBake.js';
import { InspectorProvider, MarkedField, useExpandedScene, useInspectorTarget, type InspectorTarget } from './inspector-context.js';
import { PrefabHeader, usePrefabTarget } from './PrefabInspector.js';
import { useSceneSet } from '../../state/scene-set.js';
import { OpenParticleEditorButton } from '../particles/OpenButton.js';

import { entityName, fullTransform, type EntityDesc, type Vec3 } from '../../lib/scene-doc.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useScene } from '../../state/scene.js';
import {
  Card,
  ColorField,
  EmptyState,
  IconButton,
  JsonField,
  MenuButton,
  NumberField,
  PropRow,
  SelectField,
  TextField,
  Toggle,
  VecField,
  type MenuItem,
} from '../ui/controls.js';
import { promptText } from '../../lib/prompt-dialog.js';
import { Icon } from '../ui/icons.js';
import { BUILTIN_SHADERS, COMPONENTS, COMPONENT_BY_KEY, type FieldDef } from './component-schemas.js';
import './scene-overlays.css';
import {
  addComponentCommands,
  commonComponents,
  isMixed,
  mergedTransform,
  mergeRecords,
  mergeValues,
  mixedMask,
  removeComponentCommands,
  setComponentCommands,
  setFieldCommands,
  transformCommands,
  vectorOf,
} from './multi-edit.js';

/** One typed field of a component (or any JSON object). */
export function FieldEditor({
  def,
  value,
  onChange,
  mixed,
}: {
  def: FieldDef;
  value: unknown;
  /** `index`: the vector component that changed (vec3 fields). */
  onChange: (value: unknown, index?: number) => void;
  /** Multi-selection: the value differs (vectors: per component). Complex
   * fields then show the active entity's value; an edit sets it on all. */
  mixed?: boolean | readonly boolean[];
}) {
  const body = <FieldInput def={def} value={value} onChange={onChange} mixed={mixed} />;
  const simple = ['number', 'bool', 'string', 'enum', 'color', 'vec3', 'shader', 'layer', 'path'].includes(def.type.kind);
  if (mixed !== true || simple) return body;
  return (
    <div className="mixed-field">
      <span className="mixed-note muted" title="The selected entities have different values; editing sets this value on all of them.">
        — mixed (showing the active entity)
      </span>
      {body}
    </div>
  );
}

function FieldInput({
  def,
  value,
  onChange,
  mixed,
}: {
  def: FieldDef;
  value: unknown;
  onChange: (value: unknown, index?: number) => void;
  mixed?: boolean | readonly boolean[];
}) {
  const entries = useProjectFiles((s) => s.entries);
  const t = def.type;
  const all = mixed === true;
  if (all && ['number', 'string', 'enum', 'shader', 'layer'].includes(t.kind)) value = undefined;
  switch (t.kind) {
    case 'number':
      return (
        <NumberField
          value={typeof value === 'number' ? value : undefined}
          step={t.step}
          min={t.min}
          max={t.max}
          integer={t.integer}
          onChange={onChange}
        />
      );
    case 'bool':
      return <Toggle checked={value === true} mixed={all} onChange={onChange} />;
    case 'string':
      return <TextField value={typeof value === 'string' ? value : ''} placeholder={all ? '—' : t.placeholder} onChange={(v) => onChange(v || undefined)} />;
    case 'path': {
      if (t.allowObject && value !== null && typeof value === 'object') {
        return <JsonField value={value} onChange={onChange} rows={3} />;
      }
      return (
        <AssetField
          spec={t}
          value={all ? undefined : value}
          onChange={(v) => {
            onChange(v);
            if (v) resyncSceneAssets();
          }}
        />
      );
    }
    case 'enum':
      return <SelectField value={typeof value === 'string' ? value : undefined} options={t.options} onChange={onChange} />;
    case 'color':
      return (
        <ColorField
          value={Array.isArray(value) ? (value as number[]) : undefined}
          alpha={t.alpha !== false}
          mixed={Boolean(mixed)}
          onChange={onChange}
        />
      );
    case 'vec3':
      return (
        <VecField
          value={Array.isArray(value) ? (value as number[]) : undefined}
          step={t.step ?? 0.1}
          mixed={all ? [true, true, true] : Array.isArray(mixed) ? mixed : undefined}
          onChange={onChange}
        />
      );
    case 'tags':
      return <TagsField value={Array.isArray(value) ? (value as string[]) : []} onChange={onChange} />;
    case 'layer':
      return <LayerField value={typeof value === 'string' ? value : all ? undefined : 'Default'} onChange={onChange} />;
    case 'axes': {
      const axes = Array.isArray(value) ? (value as unknown[]) : [];
      return (
        <div className="axes-field">
          {['X', 'Y', 'Z'].map((label, i) => (
            <Toggle
              key={label}
              label={label}
              checked={axes[i] === true}
              onChange={(on) => {
                const next = [0, 1, 2].map((j) => (j === i ? on : axes[j] === true));
                onChange(next.some(Boolean) ? next : undefined);
              }}
            />
          ))}
        </div>
      );
    }
    case 'shader': {
      // Built-in shading models plus the project's custom shader files.
      const files = entries.filter((e) => e.kind === 'file' && e.path.endsWith('.wgsl')).map((e) => e.path);
      const current = typeof value === 'string' && value ? value : all ? undefined : 'standard';
      const options = [...new Set<string>([...BUILTIN_SHADERS, ...files, ...(current ? [current] : [])])];
      return <SelectField value={current} options={options} onChange={onChange} />;
    }
    case 'object': {
      const obj = value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
      return (
        <div className="nested-fields">
          {t.fields.map((field) => (
            <PropRow key={field.key} label={field.label} hint={field.hint ?? field.key}>
              <FieldEditor
                def={field}
                value={obj[field.key]}
                onChange={(v) => {
                  const next = { ...obj };
                  if (v === undefined) delete next[field.key];
                  else next[field.key] = v;
                  onChange(Object.keys(next).length > 0 ? next : undefined);
                }}
              />
            </PropRow>
          ))}
        </div>
      );
    }
    case 'inputBindings':
      return <InputBindingsField axes={t.axes} value={value} onChange={onChange} />;
    case 'curve':
      return <CurveField value={value} onChange={onChange} step={t.step} min={t.min} max={t.max} modes={t.modes} plain={t.plain} />;
    case 'gradient':
      return <GradientField value={value} onChange={onChange} modes={t.modes} plain={t.plain} />;
    case 'json':
    default:
      return <JsonField value={value} onChange={onChange} rows={2} />;
  }
}

/** Select from the project's `settings.layers`; unknown names stay visible. */
function LayerField({ value, onChange }: { value: string | undefined; onChange: (value: unknown) => void }) {
  const layers = useProjectFiles((s) => s.layers);
  const options = value === undefined || layers.includes(value) ? layers : [...layers, value];
  return <SelectField value={value} options={options} onChange={onChange} />;
}

function TagsField({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  return (
    <div className="tags-field">
      {value.map((tag) => (
        <span key={tag} className="tag">
          {tag}
          <button type="button" onClick={() => onChange(value.filter((t) => t !== tag))} aria-label={`Remove ${tag}`}>
            <Icon name="x" size={10} />
          </button>
        </span>
      ))}
      <input
        className="tag-input"
        placeholder="Add tag…"
        onKeyDown={(e) => {
          const input = e.currentTarget;
          if (e.key === 'Enter' && input.value.trim()) {
            const tag = input.value.trim();
            if (!value.includes(tag)) onChange([...value, tag]);
            input.value = '';
          }
        }}
      />
    </div>
  );
}

export function ComponentCard({ entity, componentKey, value }: { entity: EntityDesc; componentKey: string; value: unknown }) {
  const target = useInspectorTarget();
  const apply = target.apply;
  const componentMark = target.mark?.(entity.id, `components.${componentKey}`) ?? null;
  const def = COMPONENT_BY_KEY.get(componentKey);
  const set = (next: unknown) => apply({ cmd: 'setComponent', id: entity.id, key: componentKey, value: next });
  const remove = () => apply({ cmd: 'removeComponent', id: entity.id, key: componentKey });

  const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
  const record = isObject ? (value as Record<string, unknown>) : {};
  const knownKeys = new Set([...(def?.fields.map((f) => f.key) ?? []), ...(componentKey === 'tilemap' ? ['size', 'layers'] : [])]);
  const extraKeys = isObject ? Object.keys(record).filter((k) => !knownKeys.has(k)) : [];

  let body;
  if (componentKey === 'tags') {
    body = <TagsField value={Array.isArray(value) ? (value as string[]) : []} onChange={set} />;
  } else if (def?.scalar) {
    body = (
      <FieldEditor def={{ key: componentKey, label: def.label, type: def.scalar }} value={value} onChange={(v) => set(v ?? def.defaults)} />
    );
  } else if (!def || !isObject || def.fields.length === 0) {
    body = <JsonField value={value} onChange={(v) => set(v ?? {})} />;
  } else {
    body = (
      <>
        {def.fields
          .filter((field) => !field.showIf || field.showIf(record))
          .map((field) => (
            <MarkedField key={field.key} id={entity.id} path={`components.${componentKey}.${field.key}`}>
              <PropRow label={field.label} hint={field.hint ?? field.key}>
                <FieldEditor
                  def={field}
                  value={record[field.key] ?? field.read?.(record)}
                  onChange={(v) => {
                    const next = { ...record };
                    if (v === undefined) delete next[field.key];
                    else next[field.key] = v;
                    for (const k of field.clears ?? []) delete next[k];
                    set(next);
                  }}
                />
              </PropRow>
            </MarkedField>
          ))}
        {componentKey === 'tilemap' && <TilemapEditor entity={entity} value={record} onChange={set} />}
        {componentKey === 'terrain' && <TerrainEditor entity={entity} value={record} onChange={set} />}
        {componentKey === 'lod' && <ImpostorBakeButton entity={entity} value={record} />}
        {componentKey === 'particleSystem' && <OpenParticleEditorButton entityId={entity.id} />}
        {componentKey === 'tilemap' && typeof record.tileset === 'string' && record.tileset && <TextureImportEditor image={record.tileset} />}
        {componentKey === 'sprite' && typeof record.image === 'string' && record.image && <TextureImportEditor image={record.image} />}
        {extraKeys.length > 0 && (
          <PropRow label="Other" hint={extraKeys.join(', ')}>
            <JsonField
              value={Object.fromEntries(extraKeys.map((k) => [k, record[k]]))}
              onChange={(v) => {
                const next: Record<string, unknown> = {};
                for (const k of Object.keys(record)) if (knownKeys.has(k)) next[k] = record[k];
                set({ ...next, ...((v as Record<string, unknown>) ?? {}) });
              }}
            />
          </PropRow>
        )}
      </>
    );
  }

  return (
    <Card
      title={def?.label ?? componentKey}
      icon={def?.icon ?? 'data'}
      subtitle={componentMark === 'inherited' ? 'from prefab' : componentMark === 'overridden' ? 'overridden' : componentMark === 'kept' ? 'kept on stop' : def ? undefined : 'custom'}
      actions={
        <MenuButton
          className="ibtn"
          title="Component options"
          items={[
            { id: 'reset', label: 'Reset to defaults', icon: 'restart', disabled: !def, run: () => def && set(def.defaults) },
            { id: 'copy', label: 'Copy JSON', icon: 'copy', run: () => void navigator.clipboard?.writeText(JSON.stringify(value, null, 2)) },
            ...(target.componentMenu?.(entity.id, componentKey) ?? []),
            { id: 'sep', label: '', separator: true },
            { id: 'remove', label: 'Remove component', icon: 'trash', danger: true, run: remove },
          ]}
        >
          <Icon name="more" size={14} />
        </MenuButton>
      }
    >
      {body}
    </Card>
  );
}

export function TransformCard({ entity }: { entity: EntityDesc }) {
  const apply = useInspectorTarget().apply;
  const t = fullTransform(entity.transform);
  const set = (key: 'position' | 'rotation' | 'scale', v: number[]) =>
    apply({ cmd: 'setTransform', id: entity.id, transform: { ...t, [key]: v as Vec3 } });
  return (
    <Card
      title="Transform"
      icon="move"
      actions={
        <IconButton
          icon="restart"
          title="Reset transform"
          size={13}
          onClick={() => apply({ cmd: 'setTransform', id: entity.id, transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] } })}
        />
      }
    >
      <MarkedField id={entity.id} path="transform.position">
        <PropRow label="Position">
          <VecField value={t.position} onChange={(v) => set('position', v)} />
        </PropRow>
      </MarkedField>
      <MarkedField id={entity.id} path="transform.rotation">
        <PropRow label="Rotation" hint="Euler XYZ, degrees">
          <VecField value={t.rotation} step={1} onChange={(v) => set('rotation', v)} />
        </PropRow>
      </MarkedField>
      <MarkedField id={entity.id} path="transform.scale">
        <PropRow label="Scale">
          <VecField value={t.scale} step={0.05} onChange={(v) => set('scale', v)} />
        </PropRow>
      </MarkedField>
    </Card>
  );
}

type Obj = Record<string, unknown>;

const SKYBOX_DEFAULTS: Record<string, Obj> = {
  procedural: { type: 'procedural', tint: [1, 1, 1], ground: [0.37, 0.35, 0.33], atmosphere: 1, sunSize: 1, exposure: 1, stars: 0 },
  color: { type: 'color', color: [0.2, 0.25, 0.3] },
  gradient: { type: 'gradient', top: [0.25, 0.45, 0.8], horizon: [0.75, 0.8, 0.85], bottom: [0.2, 0.18, 0.16] },
};

/** The scene's lighting environment: skybox, ambient, fog and sun source (like Unity's Lighting window). */
function EnvironmentInspector() {
  const doc = useScene((s) => s.doc);
  const path = useScene((s) => s.path);
  const setEnvironment = useScene((s) => s.setEnvironment);
  if (!doc) return null;
  const env = (doc.environment ?? {}) as Obj;
  const set = (key: string, value: unknown) => {
    const next: Obj = { ...env };
    if (value === undefined) delete next[key];
    else next[key] = value;
    // Older scenes: these fields are replaced by skybox / ambient objects and light entities.
    for (const old of ['sky', 'skyColor', 'timeOfDay', 'dayLengthMinutes']) delete next[old];
    setEnvironment(next);
  };
  const legacySky = env.skybox === undefined && (env.sky === 'color' || env.sky === 'none') && Array.isArray(env.skyColor);
  const skybox = (env.skybox as Obj | undefined) ?? (legacySky ? { type: 'color', color: env.skyColor } : SKYBOX_DEFAULTS.procedural!);
  const skyType = String(skybox.type ?? 'procedural');
  const setSky = (key: string, v: unknown) => set('skybox', { ...skybox, [key]: v });
  const ambient: Obj =
    typeof env.ambient === 'object' && env.ambient !== null
      ? (env.ambient as Obj)
      : { source: 'skybox', intensity: typeof env.ambient === 'number' ? env.ambient / 0.3 : 1 };
  const source = String(ambient.source ?? 'skybox');
  const setAmb = (key: string, v: unknown) => set('ambient', { ...ambient, [key]: v });
  const reflections = (env.reflections ?? {}) as Obj;
  const setRefl = (key: string, v: unknown) => set('reflections', { ...reflections, [key]: v });
  const wind = (env.wind ?? null) as Obj | null;
  const setWind = (key: string, v: unknown) => set('wind', { ...(wind ?? {}), [key]: v });
  const fog = (env.fog ?? null) as Obj | null;
  const fogMode = fog ? String(fog.mode ?? 'exp') : 'off';
  const setFog = (key: string, v: unknown) => set('fog', { mode: fogMode, color: [0.7, 0.8, 0.9], ...(fog ?? {}), [key]: v });
  const suns = doc.entities.filter((e) => {
    const l = (e.components?.light ?? null) as Obj | null;
    return l !== null && (l.kind === undefined || l.kind === 'directional');
  });
  const color = (o: Obj, key: string, fallback: number[]) => (Array.isArray(o[key]) ? (o[key] as number[]) : fallback);

  return (
    <>
      <div className="inspector-head">
        <span className="inspector-glyph">
          <Icon name="scene" size={16} />
        </span>
        <div className="inspector-titles">
          <strong>{doc.name ?? 'Scene'}</strong>
          <span className="mono muted">{path}</span>
        </div>
      </div>
      <Card title="Skybox" icon="sun">
        <PropRow label="Type">
          <SelectField
            value={skyType}
            options={['procedural', 'color', 'gradient']}
            onChange={(v) => set('skybox', SKYBOX_DEFAULTS[v] ?? SKYBOX_DEFAULTS.procedural)}
          />
        </PropRow>
        {skyType === 'procedural' && (
          <>
            <PropRow label="Tint"><ColorField value={color(skybox, 'tint', [1, 1, 1])} alpha={false} onChange={(v) => setSky('tint', v)} /></PropRow>
            <PropRow label="Ground"><ColorField value={color(skybox, 'ground', [0.37, 0.35, 0.33])} alpha={false} onChange={(v) => setSky('ground', v)} /></PropRow>
            <PropRow label="Atmosphere" hint="horizon haze and sun glow"><NumberField value={(skybox.atmosphere as number) ?? 1} step={0.05} min={0} onChange={(v) => setSky('atmosphere', v)} /></PropRow>
            <PropRow label="Sun size" hint="0 hides the sun disc"><NumberField value={(skybox.sunSize as number) ?? 1} step={0.1} min={0} onChange={(v) => setSky('sunSize', v)} /></PropRow>
            <PropRow label="Exposure"><NumberField value={(skybox.exposure as number) ?? 1} step={0.05} min={0} onChange={(v) => setSky('exposure', v)} /></PropRow>
            <PropRow label="Stars" hint="shown when the sun is down"><NumberField value={(skybox.stars as number) ?? 0} step={0.1} min={0} onChange={(v) => setSky('stars', v)} /></PropRow>
          </>
        )}
        {skyType === 'color' && (
          <PropRow label="Color"><ColorField value={color(skybox, 'color', [0, 0, 0])} alpha={false} onChange={(v) => setSky('color', v)} /></PropRow>
        )}
        {skyType === 'gradient' &&
          (['top', 'horizon', 'bottom'] as const).map((k) => (
            <PropRow key={k} label={k[0]!.toUpperCase() + k.slice(1)}>
              <ColorField value={color(skybox, k, [1, 1, 1])} alpha={false} onChange={(v) => setSky(k, v)} />
            </PropRow>
          ))}
        <PropRow label="Sun source" hint="directional light for the sky's sun and the shadows">
          <SelectField
            value={typeof env.sun === 'string' ? env.sun : ''}
            options={[{ value: '', label: 'Brightest directional light' }, ...suns.map((e) => ({ value: e.id, label: entityName(e) }))]}
            onChange={(v) => set('sun', v || undefined)}
          />
        </PropRow>
      </Card>
      <Card title="Ambient light" icon="light">
        <PropRow label="Source">
          <SelectField value={source} options={['skybox', 'gradient', 'color']} onChange={(v) => setAmb('source', v)} />
        </PropRow>
        <PropRow label="Intensity"><NumberField value={(ambient.intensity as number) ?? 1} step={0.05} min={0} onChange={(v) => setAmb('intensity', v)} /></PropRow>
        {source === 'color' && (
          <PropRow label="Color"><ColorField value={color(ambient, 'color', [0.5, 0.5, 0.5])} alpha={false} onChange={(v) => setAmb('color', v)} /></PropRow>
        )}
        {source === 'gradient' &&
          (['sky', 'equator', 'ground'] as const).map((k) => (
            <PropRow key={k} label={k[0]!.toUpperCase() + k.slice(1)}>
              <ColorField value={color(ambient, k, k === 'sky' ? [0.6, 0.7, 0.85] : k === 'equator' ? [0.45, 0.45, 0.45] : [0.2, 0.18, 0.16])} alpha={false} onChange={(v) => setAmb(k, v)} />
            </PropRow>
          ))}
      </Card>
      <Card title="Reflections" icon="light">
        <PropRow label="Source" hint="skybox: metals and glossy surfaces reflect the sky; ambient: flatter, stylized">
          <SelectField value={String(reflections.source ?? 'skybox')} options={['skybox', 'ambient', 'none']} onChange={(v) => setRefl('source', v)} />
        </PropRow>
        <PropRow label="Intensity"><NumberField value={(reflections.intensity as number) ?? 1} step={0.05} min={0} onChange={(v) => setRefl('intensity', v)} /></PropRow>
        <PropRow label="Specular occlusion" hint="0..1: ambient occlusion also dims reflections in crevices">
          <NumberField value={(reflections.specularOcclusion as number) ?? 1} step={0.05} min={0} max={1} onChange={(v) => setRefl('specularOcclusion', v)} />
        </PropRow>
      </Card>
      <Card title="Wind" icon="water">
        <PropRow label="Enabled" hint="sways trees, plants and grass (meshes with wind weights)">
          <input type="checkbox" checked={!!wind} onChange={(e) => set('wind', e.target.checked ? { strength: 0.5, direction: [1, 0, 0.3] } : undefined)} />
        </PropRow>
        {wind && (
          <>
            <PropRow label="Strength" hint="0 calm, 0.5 breeze, 1 windy, 3 storm"><NumberField value={(wind.strength as number) ?? 0.5} step={0.05} min={0} onChange={(v) => setWind('strength', v)} /></PropRow>
            <PropRow label="Direction" hint="[x, y, z] (y ignored)"><VecField value={(wind.direction as number[] | undefined) ?? [1, 0, 0.3]} onChange={(v) => setWind('direction', v)} /></PropRow>
            <PropRow label="Gusts"><NumberField value={(wind.gustStrength as number) ?? 0.5} step={0.05} min={0} onChange={(v) => setWind('gustStrength', v)} /></PropRow>
            <PropRow label="Gust frequency" hint="gusts per second"><NumberField value={(wind.gustFrequency as number) ?? 0.15} step={0.01} min={0} onChange={(v) => setWind('gustFrequency', v)} /></PropRow>
            <PropRow label="Turbulence" hint="0..1 how differently neighbouring plants move"><NumberField value={(wind.turbulence as number) ?? 0.3} step={0.05} min={0} max={1} onChange={(v) => setWind('turbulence', v)} /></PropRow>
            <PropRow label="Leaf flutter" hint="cycles per second"><NumberField value={(wind.flutter as number) ?? 6} step={0.5} min={0} onChange={(v) => setWind('flutter', v)} /></PropRow>
            <PropRow label="Characters push grass">
              <input type="checkbox" checked={wind.interaction !== false} onChange={(e) => setWind('interaction', e.target.checked)} />
            </PropRow>
          </>
        )}
      </Card>
      <Card title="Fog" icon="water">
        <PropRow label="Mode">
          <SelectField
            value={fogMode}
            options={['off', 'linear', 'exp', 'exp2']}
            onChange={(v) => (v === 'off' ? set('fog', undefined) : setFog('mode', v))}
          />
        </PropRow>
        {fog && (
          <>
            <PropRow label="Color"><ColorField value={color(fog, 'color', [0.7, 0.8, 0.9])} alpha={false} onChange={(v) => setFog('color', v)} /></PropRow>
            {fogMode === 'linear' ? (
              <>
                <PropRow label="Start" hint="meters"><NumberField value={(fog.start as number) ?? 0} step={1} min={0} onChange={(v) => setFog('start', v)} /></PropRow>
                <PropRow label="End" hint="meters"><NumberField value={(fog.end as number) ?? 300} step={1} min={0} onChange={(v) => setFog('end', v)} /></PropRow>
              </>
            ) : (
              <PropRow label="Density"><NumberField value={(fog.density as number) ?? 0.01} step={0.001} min={0} onChange={(v) => setFog('density', v)} /></PropRow>
            )}
            <PropRow label="Height" hint="fog thins out above this height"><NumberField value={(fog.height as number) ?? 0} step={1} onChange={(v) => setFog('height', v)} /></PropRow>
            <PropRow label="Height falloff" hint="0 = uniform fog"><NumberField value={(fog.heightFalloff as number) ?? 0} step={0.005} min={0} onChange={(v) => setFog('heightFalloff', v)} /></PropRow>
          </>
        )}
      </Card>
      <p className="inspector-hint muted">
        Lights are entities (Add Component → Light). Shadow distance, cascades and resolution are quality settings
        (prism.project.json → settings.quality, or set_quality() from scripts).
      </p>
      <ProjectSettings />
    </>
  );
}

/** Add Component menu items for `entities` (components some of them lack). */
function addComponentItems(entities: readonly EntityDesc[], run: (key: string, value: unknown) => void): MenuItem[] {
  const items: MenuItem[] = COMPONENTS.filter((c) => entities.some((e) => !(c.key in (e.components ?? {})))).map((c) => ({
    id: c.key,
    label: c.label,
    icon: c.icon,
    run: () => run(c.key, c.defaults),
  }));
  items.push(
    { id: 'sep', label: '', separator: true },
    {
      id: 'custom',
      label: 'Custom (JSON)…',
      icon: 'data',
      run: async () => {
        const key = await promptText('Component key');
        if (key) run(key, {});
      },
    },
  );
  return items;
}

function MultiTransformCard({ entities }: { entities: EntityDesc[] }) {
  const applyBatch = useScene((s) => s.applyBatch);
  const merged = mergedTransform(entities);
  const row = (prop: 'position' | 'rotation' | 'scale', step?: number) => (
    <VecField
      value={vectorOf(merged[prop])}
      mixed={mixedMask(merged[prop])}
      step={step}
      onChange={(v, i) => applyBatch(transformCommands(entities, prop, v, i))}
    />
  );
  return (
    <Card
      title="Transform"
      icon="move"
      actions={
        <IconButton
          icon="restart"
          title="Reset transforms"
          size={13}
          onClick={() =>
            applyBatch(entities.map((e) => ({ cmd: 'setTransform', id: e.id, transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] } })))
          }
        />
      }
    >
      <PropRow label="Position">{row('position')}</PropRow>
      <PropRow label="Rotation" hint="Euler XYZ, degrees">{row('rotation', 1)}</PropRow>
      <PropRow label="Scale">{row('scale', 0.05)}</PropRow>
    </Card>
  );
}

/** A component every selected entity has: shared values, mixed ones as —. */
function MultiComponentCard({ entities, componentKey }: { entities: EntityDesc[]; componentKey: string }) {
  const applyBatch = useScene((s) => s.applyBatch);
  const def = COMPONENT_BY_KEY.get(componentKey);
  const active = entities.at(-1)!;
  const values = entities.map((e) => e.components?.[componentKey]);
  const activeValue = active.components?.[componentKey];
  const isObject = (v: unknown) => v !== null && typeof v === 'object' && !Array.isArray(v);
  const activeRecord = isObject(activeValue) ? (activeValue as Record<string, unknown>) : {};
  const setAll = (v: unknown) => applyBatch(setComponentCommands(entities, componentKey, v));

  let body;
  if (def?.scalar || componentKey === 'tags' || !def || !values.every(isObject) || def.fields.length === 0) {
    const merged = mergeValues(values);
    const fieldDef: FieldDef = def?.scalar
      ? { key: componentKey, label: def.label, type: def.scalar }
      : componentKey === 'tags'
        ? { key: componentKey, label: 'Tags', type: { kind: 'tags' } }
        : { key: componentKey, label: componentKey, type: { kind: 'json' } };
    body = (
      <FieldEditor
        def={fieldDef}
        value={isMixed(merged) ? activeValue : vectorOf(merged) ?? merged}
        mixed={isMixed(merged) || mixedMask(merged)}
        onChange={(v) => setAll(v ?? def?.defaults ?? {})}
      />
    );
  } else {
    const merged = mergeRecords(values as Record<string, unknown>[]);
    body = def.fields
      .filter((field) => !field.showIf || field.showIf(activeRecord))
      .map((field) => {
        const m = merged[field.key];
        const shown = isMixed(m) ? (activeRecord[field.key] ?? field.read?.(activeRecord)) : (vectorOf(m) ?? m ?? field.read?.(activeRecord));
        return (
          <PropRow key={field.key} label={field.label} hint={field.hint ?? field.key}>
            <FieldEditor
              def={field}
              value={shown}
              mixed={isMixed(m) || mixedMask(m)}
              onChange={(v, index) =>
                applyBatch(setFieldCommands(entities, componentKey, field.key, v, field.type.kind === 'vec3' ? index : undefined, field.clears))
              }
            />
          </PropRow>
        );
      });
  }

  return (
    <Card
      title={def?.label ?? componentKey}
      icon={def?.icon ?? 'data'}
      subtitle={def ? `${entities.length} entities` : 'custom'}
      actions={
        <MenuButton
          className="ibtn"
          title="Component options"
          items={[
            { id: 'reset', label: 'Reset all to defaults', icon: 'restart', disabled: !def, run: () => def && setAll(def.defaults) },
            { id: 'sep', label: '', separator: true },
            {
              id: 'remove',
              label: 'Remove from all',
              icon: 'trash',
              danger: true,
              run: () => applyBatch(removeComponentCommands(entities, componentKey)),
            },
          ]}
        >
          <Icon name="more" size={14} />
        </MenuButton>
      }
    >
      {body}
    </Card>
  );
}

/** Several entities: shared components, mixed values, edits applied to all (one undo step). */
function MultiInspector({ entities }: { entities: EntityDesc[] }) {
  const applyBatch = useScene((s) => s.applyBatch);
  const deleteSelection = useScene((s) => s.deleteSelection);
  const commandError = useScene((s) => s.commandError);
  const names = mergeValues(entities.map(entityName));
  const common = commonComponents(entities);
  const partial = [...new Set(entities.flatMap((e) => Object.keys(e.components ?? {})))].filter((k) => !common.includes(k));
  const addable = addComponentItems(entities, (key, value) => applyBatch(addComponentCommands(entities, key, value)));

  return (
    <div className="inspector-body">
      <div className="inspector-head">
        <span className="inspector-glyph">
          <Icon name="layers" size={16} />
        </span>
        <div className="inspector-titles">
          <TextField
            value={isMixed(names) ? '' : String(names)}
            placeholder="—"
            onChange={(name) =>
              name.trim() && applyBatch(entities.map((e) => ({ cmd: 'renameEntity', id: e.id, name: name.trim() })))
            }
          />
          <span className="mono muted inspector-id" title={entities.map((e) => e.id).join(', ')}>
            {entities.length} entities · active: {entities.at(-1)!.id}
          </span>
        </div>
        <IconButton icon="trash" title="Delete all" size={14} onClick={() => deleteSelection()} />
      </div>

      {commandError && (
        <p className="inline-error" role="alert">
          <Icon name="warning" size={13} /> {commandError}
        </p>
      )}

      <MultiTransformCard entities={entities} />
      {common.map((key) => (
        <MultiComponentCard key={key} entities={entities} componentKey={key} />
      ))}
      {partial.length > 0 && (
        <p className="inspector-hint muted">
          Not on every selected entity (select fewer to edit): {partial.map((k) => COMPONENT_BY_KEY.get(k)?.label ?? k).join(', ')}
        </p>
      )}

      <MenuButton items={addable} className="add-component">
        <Icon name="plus" size={14} /> Add Component to all
      </MenuButton>
    </div>
  );
}

/** Unity-style inspector for the selected entity. */
export function Inspector() {
  const doc = useScene((s) => s.doc);
  const selection = useScene((s) => s.selection);
  const commandError = useScene((s) => s.commandError);
  const expanded = useExpandedScene();
  const additive = useSceneSet((s) => s.selected);
  const additiveScenes = useSceneSet((s) => s.scenes);

  // Scene entities and entities inside prefab instances (edited as overrides).
  const entity = useMemo(
    () => (selection.length === 1 ? ((expanded?.entities ?? doc?.entities ?? []).find((e) => e.id === selection[0]) ?? null) : null),
    [doc, expanded, selection],
  );
  const prefabTarget = usePrefabTarget(entity?.id ?? null, expanded);
  const selected = useMemo(
    () => (doc ? selection.map((id) => doc.entities.find((e) => e.id === id)).filter((e): e is EntityDesc => Boolean(e)) : []),
    [doc, selection],
  );
  const additiveEntity = additive ? (additiveScenes.find((s) => s.path === additive.path)?.doc?.entities.find((e) => e.id === additive.id) ?? null) : null;
  if (additive && additiveEntity) {
    const target: InspectorTarget = { apply: (c) => void useSceneSet.getState().apply(additive.path, c) };
    return (
      <InspectorProvider target={target}>
        <p className="inspector-scene-note muted">
          <span className="mono">{additive.path}</span> (additive scene)
        </p>
        <EntityInspector entity={additiveEntity} commandError={null} />
      </InspectorProvider>
    );
  }

  if (!doc) return <EmptyState icon="scene" title="No scene open" />;
  if (selected.length > 1) return <MultiInspector entities={selected} />;
  if (!entity) return <EnvironmentInspector />;

  const body = (
    <>
      <PrefabHeader id={entity.id} expanded={expanded} />
      {/* Instance roots show their inherited prefab components too. */}
      <EntityInspector entity={expanded?.instances.get(entity.id)?.root ?? entity} commandError={commandError} />
    </>
  );
  return prefabTarget ? <InspectorProvider target={prefabTarget}>{body}</InspectorProvider> : body;
}

/** One entity: name, transform, components, Add Component (edits go to the surrounding target). */
export function EntityInspector({ entity, commandError, readOnlyId }: { entity: EntityDesc; commandError: string | null; readOnlyId?: boolean }) {
  const apply = useInspectorTarget().apply;
  const components = entity.components ?? {};
  const present = Object.keys(components);
  const addable: MenuItem[] = COMPONENTS.filter((c) => !present.includes(c.key)).map((c) => ({
    id: c.key,
    label: c.label,
    icon: c.icon,
    run: () => apply({ cmd: 'setComponent', id: entity.id, key: c.key, value: c.defaults }),
  }));
  addable.push(
    { id: 'sep', label: '', separator: true },
    {
      id: 'custom',
      label: 'Custom (JSON)…',
      icon: 'data',
      run: async () => {
        const key = await promptText('Component key');
        if (key && !present.includes(key)) apply({ cmd: 'setComponent', id: entity.id, key, value: {} });
      },
    },
  );

  return (
    <div className="inspector-body">
      <div className="inspector-head">
        <span className="inspector-glyph">
          <Icon name="cube" size={16} />
        </span>
        <div className="inspector-titles">
          <TextField
            value={entityName(entity)}
            onChange={(name) => name.trim() && apply({ cmd: 'renameEntity', id: entity.id, name: name.trim() })}
          />
          <span className="mono muted inspector-id" title={readOnlyId ? 'Running entity id' : 'Stable entity id'}>
            id: {entity.id}
            {entity.parent ? ` · parent: ${entity.parent}` : ''}
          </span>
        </div>
      </div>

      {commandError && (
        <p className="inline-error" role="alert">
          <Icon name="warning" size={13} /> {commandError}
        </p>
      )}

      <TransformCard entity={entity} />
      {present.map((key) => (
        <ComponentCard key={key} entity={entity} componentKey={key} value={components[key]} />
      ))}

      <MenuButton items={addable} className="add-component">
        <Icon name="plus" size={14} /> Add Component
      </MenuButton>
    </div>
  );
}
